import { useState, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Download, DollarSign, Users, TrendingUp, Banknote, Archive, CheckCircle, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const roleLabels: Record<string, string> = {
  teacher: "مدرس/ة",
  supervisor: "مشرف/ة",
  admin: "إداري/ة",
  driver: "سائق",
  cleaner: "عامل نظافة",
};

const salaryTypeLabels: Record<string, string> = {
  monthly: "شهري",
  per_session: "بالحصة",
  percentage: "نسبة",
};

const allMonths = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

const paymentStatusLabels: Record<string, string> = {
  pending: "معلق",
  paid: "مدفوع",
  cancelled: "ملغي",
};

const SalaryReport = () => {
  const { profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const reportRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [exporting, setExporting] = useState(false);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth().toString());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear().toString());
  const monthNum = parseInt(selectedMonth) + 1;
  const yearNum = parseInt(selectedYear);

  // Current staff for generating payroll
  const { data: staffList, isLoading: staffLoading } = useQuery({
    queryKey: ["salary-report-staff", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return [];
      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name, role, salary, phone, allowances, bonuses, deductions, salary_type, session_price, commission_percentage")
        .eq("nursery_id", nurseryId)
        .order("role")
        .order("full_name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Archived payroll records for selected month
  const { data: payrollRecords, isLoading: payrollLoading } = useQuery({
    queryKey: ["payroll-records", nurseryId, monthNum, yearNum],
    queryFn: async () => {
      if (!nurseryId) return [];
      const { data, error } = await supabase
        .from("payroll_records")
        .select("*")
        .eq("nursery_id", nurseryId)
        .eq("month", monthNum)
        .eq("year", yearNum)
        .order("staff_role")
        .order("staff_name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const hasArchive = (payrollRecords?.length || 0) > 0;

  // Session counts and commission revenues for non-monthly staff (editable before archiving)
  const [sessionCounts, setSessionCounts] = useState<Record<string, number>>({});
  const [commissionRevenues, setCommissionRevenues] = useState<Record<string, number>>({});

  const calcNetSalary = (s: any, sessCount?: number, commRevenue?: number) => {
    const type = s.salary_type || "monthly";
    const allowances = Number(s.allowances || 0);
    const bonuses = Number(s.bonuses || 0);
    const deductions = Number(s.deductions || 0);
    let base = 0;
    if (type === "monthly") {
      base = Number(s.salary || 0);
    } else if (type === "per_session") {
      base = (sessCount ?? 0) * Number(s.session_price || 0);
    } else if (type === "percentage") {
      base = (commRevenue ?? 0) * Number(s.commission_percentage || 0) / 100;
    }
    return base + allowances + bonuses - deductions;
  };

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (!nurseryId || !staffList?.length) throw new Error("لا يوجد موظفين");
      const records = staffList.map(s => {
        const type = s.salary_type || "monthly";
        const sessCount = sessionCounts[s.id] || 0;
        const commRevenue = commissionRevenues[s.id] || 0;
        const net = calcNetSalary(s, sessCount, commRevenue);
        return {
          nursery_id: nurseryId,
          staff_id: s.id,
          staff_name: s.full_name,
          staff_role: s.role,
          month: monthNum,
          year: yearNum,
          base_salary: type === "monthly" ? Number(s.salary || 0) : (type === "per_session" ? sessCount * Number(s.session_price || 0) : commRevenue * Number(s.commission_percentage || 0) / 100),
          allowances: Number(s.allowances || 0),
          bonuses: Number(s.bonuses || 0),
          deductions: Number(s.deductions || 0),
          net_salary: net,
          salary_type: type,
          sessions_count: sessCount,
          session_price: Number(s.session_price || 0),
          commission_percentage: Number(s.commission_percentage || 0),
          commission_revenue: commRevenue,
          created_by: undefined,
        };
      });
      const { error } = await supabase.from("payroll_records").upsert(records as any, { onConflict: "nursery_id,staff_id,month,year" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payroll-records"] });
      toast({ title: "تم تسجيل الرواتب", description: `تم أرشفة رواتب شهر ${allMonths[parseInt(selectedMonth)]} ${selectedYear}` });
    },
    onError: (e: any) => toast({ title: "خطأ", description: e.message, variant: "destructive" }),
  });

  // Mark as paid mutation
  const markPaidMutation = useMutation({
    mutationFn: async (recordId: string) => {
      const { error } = await supabase.from("payroll_records").update({ status: "paid", paid_at: new Date().toISOString() }).eq("id", recordId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payroll-records"] });
      toast({ title: "تم تحديث الحالة" });
    },
  });

  // Use archive data if available, otherwise use live staff data
  const displayData = useMemo(() => {
    if (hasArchive) {
      return payrollRecords!.map(r => ({
        id: r.id,
        name: r.staff_name,
        role: r.staff_role,
        salaryType: (r as any).salary_type || "monthly",
        base: Number(r.base_salary),
        allow: Number(r.allowances),
        bonus: Number(r.bonuses),
        deduct: Number(r.deductions),
        net: Number(r.net_salary),
        sessionsCount: (r as any).sessions_count || 0,
        sessionPrice: (r as any).session_price || 0,
        commissionPct: (r as any).commission_percentage || 0,
        commissionRevenue: (r as any).commission_revenue || 0,
        status: r.status,
        isArchived: true,
      }));
    }
    return (staffList || []).map(s => {
      const type = (s as any).salary_type || "monthly";
      const sessCount = sessionCounts[s.id] || 0;
      const commRevenue = commissionRevenues[s.id] || 0;
      const net = calcNetSalary(s, sessCount, commRevenue);
      return {
        id: s.id,
        name: s.full_name,
        role: s.role,
        salaryType: type,
        base: type === "monthly" ? Number(s.salary || 0) : (type === "per_session" ? sessCount * Number((s as any).session_price || 0) : commRevenue * Number((s as any).commission_percentage || 0) / 100),
        allow: Number(s.allowances || 0),
        bonus: Number(s.bonuses || 0),
        deduct: Number(s.deductions || 0),
        net,
        sessionsCount: sessCount,
        sessionPrice: Number((s as any).session_price || 0),
        commissionPct: Number((s as any).commission_percentage || 0),
        commissionRevenue: commRevenue,
        status: "pending" as string,
        isArchived: false,
      };
    });
  }, [hasArchive, payrollRecords, staffList, sessionCounts, commissionRevenues]);

  const stats = useMemo(() => {
    const totalBase = displayData.reduce((s, d) => s + d.base, 0);
    const totalAllow = displayData.reduce((s, d) => s + d.allow, 0);
    const totalBonus = displayData.reduce((s, d) => s + d.bonus, 0);
    const totalDeduct = displayData.reduce((s, d) => s + d.deduct, 0);
    const totalNet = displayData.reduce((s, d) => s + d.net, 0);
    const byRole: Record<string, { count: number; total: number }> = {};
    displayData.forEach(d => {
      if (!byRole[d.role]) byRole[d.role] = { count: 0, total: 0 };
      byRole[d.role].count++;
      byRole[d.role].total += d.net;
    });
    const paidCount = displayData.filter(d => d.status === "paid").length;
    return { total: displayData.length, totalBase, totalAllow, totalBonus, totalDeduct, totalNet, byRole, paidCount };
  }, [displayData]);

  const isLoading = staffLoading || payrollLoading;
  const monthLabel = allMonths[parseInt(selectedMonth)];
  const reportTitle = `تقرير رواتب شهر ${monthLabel} ${selectedYear}`;

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`salary-report-${selectedYear}-${monthNum.toString().padStart(2, "0")}.pdf`);
    } catch (err) {
      console.error("PDF export error:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">تقرير الرواتب</h1>
          <p className="text-muted-foreground text-sm mt-1">تقرير رواتب الموظفين الشهري مع الأرشفة والتصدير</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {allMonths.map((m, i) => (
                <SelectItem key={i} value={i.toString()}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!hasArchive && (
            <Button onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending || isLoading || !staffList?.length} className="bg-primary text-primary-foreground">
              {archiveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Archive className="w-4 h-4 ml-2" />}
              تسجيل رواتب الشهر
            </Button>
          )}
          <Button onClick={handleExportPDF} disabled={exporting || isLoading} variant="outline">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Download className="w-4 h-4 ml-2" />}
            تصدير PDF
          </Button>
        </div>
      </div>

      {hasArchive && (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
          <Archive className="w-3 h-3 ml-1" /> مؤرشف - سجل رواتب {monthLabel} {selectedYear}
        </Badge>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "عدد الموظفين", value: stats.total, icon: Users, color: "text-primary" },
          { label: "إجمالي صافي الرواتب", value: `${stats.totalNet.toLocaleString()} ج.م`, icon: DollarSign, color: "text-primary" },
          { label: "تم الدفع", value: `${stats.paidCount} / ${stats.total}`, icon: CheckCircle, color: "text-green-600" },
          { label: "إجمالي الخصومات", value: `${stats.totalDeduct.toLocaleString()} ج.م`, icon: Banknote, color: "text-destructive" },
        ].map((s, i) => (
          <Card key={i} className="shadow-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <div className="text-xl font-bold text-foreground">{isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Printable Report */}
      <div ref={reportRef} className="bg-background">
        {/* Role Breakdown */}
        <Card className="shadow-card mb-4">
          <CardHeader>
            <CardTitle className="text-lg">توزيع الرواتب حسب الوظيفة</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(stats.byRole).map(([role, data]) => (
                  <div key={role} className="border rounded-lg p-3 text-center">
                    <Badge variant="outline" className="mb-2">{roleLabels[role] || role}</Badge>
                    <div className="text-lg font-bold text-foreground">{data.total.toLocaleString()} ج.م</div>
                    <div className="text-xs text-muted-foreground">{data.count} موظف</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Staff Salary Table */}
        <Card className="shadow-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{reportTitle}</CardTitle>
            <span className="text-xs text-muted-foreground">تاريخ الطباعة: {format(new Date(), "dd/MM/yyyy", { locale: ar })}</span>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : !displayData.length ? (
              <div className="py-12 text-center text-muted-foreground">لا يوجد موظفين مسجلين</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-right p-3 font-medium text-muted-foreground w-12">#</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">اسم الموظف</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">الوظيفة</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">النوع</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">الراتب الأساسي</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">البدلات</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">العلاوات</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">الخصومات</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">صافي الراتب</th>
                      <th className="text-right p-3 font-medium text-muted-foreground">الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.map((d, i) => (
                      <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="p-3 text-muted-foreground">{i + 1}</td>
                        <td className="p-3 font-medium text-card-foreground">{d.name}</td>
                        <td className="p-3"><Badge variant="outline">{roleLabels[d.role] || d.role}</Badge></td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-[10px]">{salaryTypeLabels[d.salaryType] || "شهري"}</Badge>
                          {d.salaryType === "per_session" && !d.isArchived && (
                            <div className="mt-1">
                              <Input
                                type="number"
                                placeholder="عدد الحصص"
                                className="h-7 w-20 text-xs"
                                dir="ltr"
                                value={sessionCounts[d.id] || ""}
                                onChange={e => setSessionCounts(prev => ({ ...prev, [d.id]: Number(e.target.value) || 0 }))}
                              />
                              <span className="text-[10px] text-muted-foreground">× {d.sessionPrice}</span>
                            </div>
                          )}
                          {d.salaryType === "per_session" && d.isArchived && (
                            <span className="text-[10px] text-muted-foreground block">{d.sessionsCount} حصة × {d.sessionPrice}</span>
                          )}
                          {d.salaryType === "percentage" && !d.isArchived && (
                            <div className="mt-1">
                              <Input
                                type="number"
                                placeholder="الإيرادات"
                                className="h-7 w-24 text-xs"
                                dir="ltr"
                                value={commissionRevenues[d.id] || ""}
                                onChange={e => setCommissionRevenues(prev => ({ ...prev, [d.id]: Number(e.target.value) || 0 }))}
                              />
                              <span className="text-[10px] text-muted-foreground">× {d.commissionPct}%</span>
                            </div>
                          )}
                          {d.salaryType === "percentage" && d.isArchived && (
                            <span className="text-[10px] text-muted-foreground block">{Number(d.commissionRevenue).toLocaleString()} × {d.commissionPct}%</span>
                          )}
                        </td>
                        <td className="p-3 text-muted-foreground">{d.base ? `${d.base.toLocaleString()}` : "-"}</td>
                        <td className="p-3 text-primary">{d.allow ? `+${d.allow.toLocaleString()}` : "-"}</td>
                        <td className="p-3 text-primary">{d.bonus ? `+${d.bonus.toLocaleString()}` : "-"}</td>
                        <td className="p-3 text-destructive">{d.deduct ? `-${d.deduct.toLocaleString()}` : "-"}</td>
                        <td className="p-3 font-bold text-foreground">{d.net ? `${d.net.toLocaleString()} ج.م` : "-"}</td>
                        <td className="p-3">
                          {d.isArchived ? (
                            d.status === "paid" ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                <CheckCircle className="w-3 h-3 ml-1" /> مدفوع
                              </Badge>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => markPaidMutation.mutate(d.id)} disabled={markPaidMutation.isPending}>
                                <Clock className="w-3 h-3 ml-1" /> تأكيد الدفع
                              </Button>
                            )
                          ) : (
                            <span className="text-xs text-muted-foreground">غير مؤرشف</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/70 border-t-2">
                      <td colSpan={4} className="p-3 font-bold text-foreground">الإجمالي</td>
                      <td className="p-3 font-bold text-foreground">{stats.totalBase.toLocaleString()}</td>
                      <td className="p-3 font-bold text-primary">+{stats.totalAllow.toLocaleString()}</td>
                      <td className="p-3 font-bold text-primary">+{stats.totalBonus.toLocaleString()}</td>
                      <td className="p-3 font-bold text-destructive">-{stats.totalDeduct.toLocaleString()}</td>
                      <td className="p-3 font-bold text-primary">{stats.totalNet.toLocaleString()} ج.م</td>
                      <td className="p-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalaryReport;
